import { Space } from 'antd';
import { Content } from 'antd/es/layout/layout';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { EditButtonIcon, XIcon } from 'assets/svg';
import { Button, ButtonAction, Switch } from 'components';
import {
  IAllocatePriceParams,
  IDataGrouped,
  IGoldSoldBuyOrder,
  IGoldSoldYBuyOrder,
  IOrder,
  IPromotion,
  IPromotionResult,
  Result,
} from 'interfaces/SalesInfo';
import formatNumber, {
  formatCurrency,
  groupBy,
  toastError,
} from 'utils/constants/functions';
import CustomCollapse, {
  IPanelConfig,
} from '../../components/Collapse/CustomCollapse';
import { ManualPromotionModal } from './ModalPromotion/Manual';
import {
  IUpdatePromotionData,
  UpdateSAPPromotionModal,
} from './ModalPromotion/UpdateSAPPromotion';
import { VoucherModal } from './ModalPromotion/Voucher';
import { useUser } from 'contexts/UserProvider';
import { IPromotionExtended } from 'interfaces/SalesInfo';

enum SelectModal {
  VOUCHER = 'VOUCHER',
  MANUALPROMOTION = 'PROMOTIONS',
  UPDATESAPPROMOTION = 'UPDATESAPPROMOTION',
}

interface IPromotionalProgramTabProps {
  orderData: IOrder;
  isChildrenOrder?: boolean;
  hasEInvoice?: boolean;
  hasSODOBilling?: boolean;
  goldSoldBuyOrderData: IGoldSoldBuyOrder | IGoldSoldYBuyOrder;
  isOrderOfAccountant: boolean;
  promotion: {
    data: IPromotion;
    isPending: boolean;
  };
  table: {
    data: {
      changeCTKM: boolean;
      SAPPromotions: IDataGrouped[];
      vouchers: IPromotionResult['results'];
      manualVouchers: IDataGrouped[];
    };
    setData: (data: {
      changeCTKM: boolean;
      SAPPromotions: IDataGrouped[];
      vouchers: IPromotionResult['results'];
      manualVouchers: IDataGrouped[];
    }) => void;
  };
  onApplyVouchers(params: IAllocatePriceParams): void;
  onTakeSAPPromotionProgram(
    params: IAllocatePriceParams,
    beforeMutation?: () => void,
  ): void;
}

export const PromotionalProgramTab = memo(
  ({
    table,
    orderData,
    promotion,
    hasEInvoice,
    hasSODOBilling,
    isOrderOfAccountant,
    isChildrenOrder = false,
    goldSoldBuyOrderData,
    onApplyVouchers,
    onTakeSAPPromotionProgram,
  }: IPromotionalProgramTabProps) => {
    const { user } = useUser();

    const { data: promotionData, isPending: isGetPromotionDataPending } =
      promotion;
    const { data: tableData, setData: setTableData } = table;

    const [isModalVisible, setIsModalVisible] = useState<SelectModal>(),
      [SAPPromotionsSelected, setSAPPromotionsSelected] =
        useState<IUpdatePromotionData>(),
      [selectedManualVoucher, setSelectedManualVoucher] = useState<
        IDataGrouped | undefined
      >(undefined);

    const isDisableAction = useMemo(() => {
      return (
        user?.isAdmin ||
        hasSODOBilling ||
        isChildrenOrder ||
        !isOrderOfAccountant ||
        promotionData?.IsEInvoicePending ||
        promotionData?.IsAllocatePricePending
      );
    }, [
      user,
      hasSODOBilling,
      isChildrenOrder,
      promotionData,
      isOrderOfAccountant,
    ]);

    const renderActions = useCallback(
      (tableName: string, record?: any) => (
        <Space size="middle">
          {tableName === 'CTKM từ SAP' ? (
            <>
              <ButtonAction
                disabled={isDisableAction}
                tooltip="Chỉnh sửa"
                onClick={() => {
                  setSAPPromotionsSelected({
                    ...record,
                    records: record?.records.map(
                      (item: Result, index: number) => ({
                        ...item,
                        id: index + 1,
                      }),
                    ),
                  });
                  setIsModalVisible(SelectModal.UPDATESAPPROMOTION);
                }}
              >
                <EditButtonIcon />
              </ButtonAction>
              <Switch
                disabled={isDisableAction}
                checked={
                  record.records
                    .map((item: Result) => item.apply)
                    .includes(true)
                    ? true
                    : false
                }
                onChange={(checked) => {
                  setTableData({
                    ...tableData,
                    changeCTKM: true,
                    SAPPromotions: tableData.SAPPromotions.map((item) => {
                      if (item?.ConditionType === record?.ConditionType)
                        return {
                          ...item,
                          records: item.records.map((record) => ({
                            ...record,
                            apply: checked,
                          })),
                        };
                      return item;
                    }),
                  });
                }}
              />
            </>
          ) : tableName === 'manual' ? (
            <>
              <ButtonAction
                disabled={isDisableAction}
                tooltip="Chỉnh sửa"
                onClick={() => {
                  setSelectedManualVoucher(record);
                  setIsModalVisible(SelectModal.MANUALPROMOTION);
                }}
              >
                <EditButtonIcon />
              </ButtonAction>
              <ButtonAction
                disabled={isDisableAction}
                tooltip="Xóa"
                onClick={() => {
                  setTableData({
                    ...tableData,
                    changeCTKM: true,
                    manualVouchers: tableData.manualVouchers.reduce(
                      (acc, item) => {
                        if (item?.ConditionType === record?.ConditionType) {
                          const currentRecords = item.records.slice(1);

                          if (currentRecords.length <= 0) return acc;

                          const [firstE] = currentRecords;

                          acc.push({
                            ...item,
                            Note: firstE?.Note,
                            ConditionValue: firstE?.ConditionValue,
                            records: currentRecords,
                          });

                          return acc;
                        }

                        acc.push(item);
                        return acc;
                      },
                      [] as any,
                    ),
                  });
                }}
              >
                <XIcon />
              </ButtonAction>
            </>
          ) : (
            <ButtonAction
              disabled={isDisableAction}
              tooltip="Xóa"
              onClick={() => {
                setTableData({
                  ...tableData,
                  changeCTKM: true,
                  vouchers: tableData.vouchers.filter(
                    (item) => item?.SaleDeal !== record?.SaleDeal,
                  ),
                });
              }}
            >
              <XIcon />
            </ButtonAction>
          )}
        </Space>
      ),
      [tableData, isDisableAction],
    );

    const panels = useMemo(
      () =>
        [
          {
            tableProps: {
              scroll: { x: 2000 },
              loading: isGetPromotionDataPending,
              rowKey: (record) => record?.ConditionType ?? '',
            },
            title: 'CTKM từ SAP',
            key: 'sap',
            columns: [
              {
                title: 'STT',
                width: 50,
                render(_, __, index) {
                  return index + 1;
                },
              },
              {
                title: 'Mã chương trình',
                width: 150,
                dataIndex: 'ConditionNum',
              },
              { title: 'Chương trình', dataIndex: 'ConditionName', width: 150 },
              {
                title: 'ConditionType',
                dataIndex: 'ConditionType',
                width: 80,
                render: (value, record) => {
                  return record?.Type?.toLowerCase() === 'percent'
                    ? `${value} - %`
                    : `${value} - giá trị`;
                },
              },
              {
                title: 'Giá trị/phần trăm CK',
                width: 100,
                dataIndex: 'ConditionValue',
                align: 'right',
                currency: 'vi-VN',
                render: (value, record) => {
                  try {
                    const [newValue, ..._] = value.split('-');

                    return record?.Type?.toLowerCase() === 'percent'
                      ? formatNumber(newValue)
                      : formatCurrency({ value: newValue, round: true });
                  } catch {
                    return '-';
                  }
                },
              },
              {
                title: 'Đơn vị',
                width: 100,
                render: (_, record) => {
                  return record.Type === 'Percent'
                    ? '%'
                    : (record?.Currency ?? 'VND');
                },
              },
              {
                title: 'Sản phẩm áp dụng',
                width: 250,
                render: (_, record) => {
                  return record.records
                    .reduce((acc: string[], item: Result) => {
                      if (!item?.apply) return acc;
                      acc.push(item?.Material ?? '');
                      return acc;
                    }, [])
                    .join(', ');
                },
              },
              {
                title: 'Sử dụng',
                dataIndex: 'apply',
                width: 150,
                render: (_, record) => {
                  return record.records
                    .map((item: Result) => item.apply)
                    .includes(true)
                    ? 'Áp dụng'
                    : 'Không áp dụng';
                },
              },
              {
                title: 'Thao tác',
                fixed: 'right' as const,
                width: 100,
                align: 'center',
                key: 'action',
                render: (_, record) => renderActions('CTKM từ SAP', record),
              },
            ],
            data: tableData.SAPPromotions,
          },
          {
            tableProps: {
              scroll: { x: 2000 },
              loading: isGetPromotionDataPending,
            },
            title: 'Phiếu ưu đãi',
            key: 'voucher',
            columns: [
              {
                title: 'STT',
                width: 50,
                render: (_, __, index) => index + 1,
              },
              {
                title: 'Mã chương trình',
                dataIndex: 'SaleDeal',
                width: 150,
              },
              { title: 'Chương trình', dataIndex: 'VoucherName', width: 350 },
              {
                title: 'ConditionType',
                dataIndex: 'ConditionType',
                width: 150,
                render: (value, record) => {
                  return record?.Type?.toLowerCase() === 'percent'
                    ? `${value} - %`
                    : `${value} - giá trị`;
                },
              },
              {
                title: 'Giá trị/phần trăm CK',
                dataIndex: 'ConditionValue',
                align: 'right',
                currency: 'vi-VN',
                width: 150,
                render: (value, record) => {
                  const [newValue, ..._] = value
                    ? value?.split('-')
                    : [undefined];

                  return record?.Type?.toLowerCase() === 'percent'
                    ? formatNumber(newValue)
                    : formatCurrency({ value: newValue, round: true });
                },
              },
              { title: 'Serial', dataIndex: 'Serial', width: 150 },
              {
                title: 'Ghi chú',
                dataIndex: 'Note',
                width: 350,
                render: (value) => {
                  return value ? value : '-';
                },
              },
              {
                title: 'Thời gian hiệu lực',
                width: 200,
                render: (_, record) => {
                  const isValid =
                    dayjs(record?.SerialFromDate).isValid() &&
                    dayjs(record?.SerialToDate).isValid();

                  return isValid
                    ? `${dayjs(record?.SerialFromDate).format('DD/MM/YYYY')} - ${dayjs(record?.SerialToDate).format('DD/MM/YYYY')}`
                    : '-';
                },
              },
              {
                title: 'Thao tác',
                fixed: 'right' as const,
                width: 80,
                align: 'center',
                key: 'action',
                render: (_, record) => renderActions('', record),
              },
            ],
            data: tableData.vouchers,
          },
          {
            tableProps: {
              scroll: { x: 2000 },
              loading: isGetPromotionDataPending,
              rowKey: (record) => record?.ConditionType ?? '',
            },
            title: 'Nhập tay',
            key: 'manual',
            columns: [
              {
                title: 'STT',
                width: 50,
                render: (_, __, index) => index + 1,
              },
              {
                title: 'Mã chương trình',
                dataIndex: 'SaleDeal',
                width: 150,
              },
              { title: 'Chương trình', dataIndex: 'SaleDealName', width: 350 },
              {
                title: 'ConditionType',
                dataIndex: 'ConditionType',
                width: 150,
                render: (value, record) => {
                  return record?.Type?.toLowerCase() === '%'
                    ? `${value} - %`
                    : `${value} - giá trị`;
                },
              },
              {
                title: 'Giá trị/phần trăm CK',
                dataIndex: 'ConditionValue',
                align: 'right',
                currency: 'vi-VN',
                width: 150,
                render: (value, record) => {
                  try {
                    const [newValue, ..._] = value.split('-');

                    return record?.Type?.toLowerCase() === '%'
                      ? formatNumber(newValue)
                      : formatCurrency({ value: newValue, round: true });
                  } catch {
                    return record?.Type?.toLowerCase() === '%'
                      ? formatNumber(value)
                      : formatCurrency({ value: value, round: true });
                  }
                },
              },
              {
                title: 'Sản phẩm áp dụng',
                width: 300,
                render: (_, record) => {
                  return (record?.records ?? [])
                    .map((item: Result) => item?.Material ?? '')
                    .join(', ');
                },
              },
              { title: 'Ghi chú', dataIndex: 'Note', width: 350 },
              {
                title: 'Thao tác',
                fixed: 'right' as const,
                width: 100,
                align: 'center',
                key: 'action',
                render: (_, record) => renderActions('manual', record),
              },
            ],
            data: tableData.manualVouchers,
          },
        ] as IPanelConfig[],
      [promotionData, isGetPromotionDataPending, tableData],
    );

    useEffect(() => {
      if (!promotionData) return;

      let manualVouchers: any = [];
      const vouchers = (promotionData?.PromoHeader?.results ?? []).reduce(
        (acc, data) => {
          const index = promotionData?.Voucher?.results.findIndex(
            (voucher) => voucher?.SaleDeal === data?.SaleDeal,
          );

          if (index < 0) {
            manualVouchers.push(data);
            return acc;
          }

          acc.push(data);
          return acc;
        },
        [] as any,
      );

      manualVouchers = groupBy(
        [...(promotionData?.PromoItem?.results ?? []), ...manualVouchers],
        {
          groupBy: 'ConditionType',
          keys: [
            'Batch',
            'ConditionType',
            'ConditionValue',
            'DiscountValue',
            'Item',
            'Key',
            'Material',
            'Note',
            'ProcessPrice',
            'ProcessPriceSAP',
            'SaleDeal',
            'SaleDealName',
            'TextScan',
            'Type',
            'Serial',
          ],
        },
      );

      const promotionPrograms = groupBy(
        promotionData?.OutputDiscount?.results ?? [],
        {
          groupBy: 'ConditionType',
          keys: [
            'ConditionNum',
            'ConditionType',
            'ConditionName',
            'ConditionValue',
            'Type',
          ],
        },
      );

      setTableData({
        changeCTKM: false,
        SAPPromotions: promotionPrograms,
        vouchers: vouchers,
        manualVouchers: manualVouchers,
      });
    }, [promotionData]);

    const productsApplied = useMemo(() => {
      return tableData.manualVouchers.reduce((acc, voucher) => {
        acc.push(...voucher.records);
        return acc;
      }, [] as Result[]);
    }, [tableData]);

    const handleOpenModal = useCallback(
      (modalType: SelectModal) => {
        console.log('handleOpenModal', promotionData);

        if (
          promotionData?.Option === undefined ||
          promotionData?.Option === null
        ) {
          return toastError('Vui lòng lấy CTKM từ SAP trước!');
        }
        setIsModalVisible(modalType);
      },
      [promotionData],
    );

    const handleCloseModal = useCallback(() => {
      setSelectedManualVoucher(undefined);
      setIsModalVisible(undefined);
    }, []);

    const handleApplyVouchers = useCallback(() => {
      const conditionInsert: IPromotionResult['results'] = [];
      const conditionDelete: IPromotionResult['results'] = [];

      tableData.SAPPromotions.forEach((item) => {
        item.records.forEach((item) => {
          const condition = {
            CondCount: item.CondCount,
            ConditionType: item.ConditionType,
            ConditionValue: item.ConditionValue,
            Item: item.Item,
            Key: item.Key,
          };

          if (item.apply) {
            conditionInsert.push(condition);
            return;
          }

          conditionDelete.push(condition);
        });
      });

      const params = {
        ...promotionData,
        Option: 'M',
        ConditionInsert: { results: conditionInsert },
        ConditionDelete: { results: conditionDelete },
        OutputDiscount: {
          results: (promotionData?.OutputDiscount?.results ?? []).map(
            (result) => {
              const isNotApply =
                conditionInsert.findIndex(
                  (condition) =>
                    condition?.Item === result?.Item &&
                    condition?.ConditionType === result?.ConditionType,
                ) < 0;

              return { ...result, apply: !isNotApply };
            },
          ),
        },
        PromoHeader: {
          results: [
            ...tableData.vouchers,
            ...tableData.manualVouchers.reduce((acc, item) => {
              if (item?.ConditionType.includes('H')) {
                item?.records.forEach((item) => {
                  try {
                    const { id, ...others } = item;
                    acc.push(others);
                  } catch {
                    acc.push(item);
                  }
                });
              }

              return acc;
            }, [] as any),
          ],
        },
        Voucher: {
          results: tableData.vouchers.map((item) => {
            return {
              ...item,
              Item: '000000',
              Key: orderData?.id,
            };
          }) as any,
        },
        PromoItem: {
          results: tableData.manualVouchers.reduce((acc, item) => {
            item.records.forEach((item) => {
              try {
                if (item?.ConditionType?.includes('I')) {
                  const { id, ...others } = item;
                  acc.push(others);
                }
              } catch (error) {
                console.log(error);
                acc.push(item);
              }
            });

            return acc;
          }, [] as Result[]),
        },
      } as IAllocatePriceParams;

      onApplyVouchers(params);
    }, [tableData, orderData, promotionData]);

    const handleConfirmUpdateSAPPromotion = useCallback(
      (record: IUpdatePromotionData) => {
        const newData = {
          ...tableData,
          changeCTKM: true,
          SAPPromotions: tableData.SAPPromotions.map((item) => {
            if (item?.ConditionType === record.ConditionType)
              return { ...item, records: record?.records };
            return item;
          }),
        };
        setTableData(newData);

        setIsModalVisible(undefined);
      },
      [tableData, setTableData],
    );

    const handleBeforeConfirmVoucher = useCallback(
      (values: any) => {
        const vouchers = [
          ...tableData.vouchers,
          ...tableData.manualVouchers.filter((voucher) =>
            voucher?.ConditionType.includes('H'),
          ),
        ];

        if (
          vouchers.findIndex((voucher) => voucher?.Serial === values?.Serial) >=
          0
        ) {
          toastError('Phiếu ưu đãi đã tồn tại!');
          return { isExist: true };
        }

        return { isExist: false };
      },
      [tableData],
    );

    const handleConfirmVoucher = useCallback(
      (values: any) => {
        setTableData({
          ...tableData,
          changeCTKM: true,
          vouchers: [...tableData.vouchers, values],
        });
      },
      [tableData],
    );

    const handleBeforeConfirmManualVouchers = useCallback(
      (record: any) => {
        const isExistManual =
          tableData.manualVouchers
            .filter(
              (voucher) =>
                voucher?.ConditionType !== selectedManualVoucher?.ConditionType,
            )
            .findIndex((voucher) =>
              voucher?.ConditionType?.includes(
                record?.ConditionType?.match(/H|I/)[0],
              ),
            ) >= 0;
        // const isExistManual =
        //   tableData.manualVouchers
        //     .filter(
        //       (voucher) =>
        //         voucher?.ConditionType !== selectedManualVoucher?.ConditionType,
        //     )
        //     .findIndex(
        //       (voucher) => voucher?.ConditionType === record?.ConditionType,
        //     ) >= 0;
        const isExistVoucher =
          tableData.vouchers.findIndex(
            (voucher) => voucher?.SaleDeal === record?.SaleDeal,
          ) >= 0;

        if (isExistManual || isExistVoucher) {
          // if (
          //   ((record?.ConditionType?.includes('H') ||
          //     record?.ConditionType?.includes('I')) ??
          //     false) &&
          //   // isExistVoucher
          //   (isExistManual || isExistVoucher)
          // ) {
          toastError('Phiếu ưu đãi đã tồn tại!');
          return { isExist: true };
        }

        return { isExist: false };
      },
      [tableData, selectedManualVoucher],
    );

    const handleConfirmManualVouchers = useCallback(
      (records: any) => {
        let manualVouchers;

        if (!selectedManualVoucher) {
          console.log('add');

          manualVouchers = [
            ...tableData.manualVouchers.reduce((acc, manualVoucher) => {
              acc.push(...manualVoucher.records);
              return acc;
            }, [] as Result[]),
            ...records,
          ];
        } else {
          console.log('edit');

          const index = tableData.manualVouchers.findIndex(
            (voucher) =>
              voucher?.ConditionType === selectedManualVoucher?.ConditionType,
          );

          manualVouchers = tableData?.manualVouchers
            .map((item, currentIndex) => {
              if (index === currentIndex)
                return { ...records[0], records: records };

              return item;
            })
            .reduce((acc, item: IDataGrouped) => {
              acc.push(...item?.records);
              return acc;
            }, [] as Result[]);
        }

        if (manualVouchers) {
          setTableData({
            ...tableData,
            changeCTKM: true,
            manualVouchers: groupBy(manualVouchers, {
              groupBy: 'ConditionType',
              keys: [
                'ConditionType',
                'ConditionValue',
                'Note',
                'SaleDeal',
                'SaleDealName',
                'Serial',
                'Type',
              ],
            }),
          });
        }
      },
      [tableData, selectedManualVoucher],
    );

    const isDisabledButton = useMemo(() => {
      return (
        hasEInvoice || promotionData?.IsAllocatePricePending || isChildrenOrder
      );
    }, [hasEInvoice, promotionData, isChildrenOrder]);

    const handleOnTakAllocatePrice = useCallback(() => {
      const params = {
        ...(
          [
            'OrderType',
            'SaleOrg',
            'SaleOffice',
            'Channel',
            'Division',
            'SalePerson',
            'DealPrice',
            'Key',
            'Customer',
            'Item',
          ] as Array<keyof IPromotion>
        ).reduce((acc, key) => {
          acc[key] = promotionData[key];

          return acc;
        }, {} as any),
        Option: 'A',
      };
      console.log('handleOnTakAllocatePrice', params);
      onTakeSAPPromotionProgram(params);
    }, [promotionData, onTakeSAPPromotionProgram]);

    return (
      <Content className="overflow-x-auto">
        <div className=" flex justify-between items-center">
          <h2 className="text-xl font-bold">
            Tổng khuyến mãi:
            {` ${
              formatCurrency({
                value: orderData?.totalDiscount,
                round: true,
              }) || 0
            } vnd`}
          </h2>
          <div className="flex space-x-2">
            {!user?.isAdmin && (
              <Button
                title="CTKM từ SAP"
                disabled={
                  isDisabledButton ||
                  (goldSoldBuyOrderData?.orderTempDetail ?? []).length <= 0 ||
                  hasSODOBilling ||
                  !isOrderOfAccountant
                }
                handleOnclick={handleOnTakAllocatePrice}
              />
            )}
            {!user?.isAdmin && (
              <Button
                disabled={
                  isDisabledButton ||
                  (goldSoldBuyOrderData?.orderTempDetail ?? []).length <= 0 ||
                  hasSODOBilling ||
                  !isOrderOfAccountant
                }
                title="Phiếu ưu đãi"
                handleOnclick={() => handleOpenModal(SelectModal.VOUCHER)}
              />
            )}
            {!user?.isAdmin && (
              <Button
                disabled={
                  isDisabledButton ||
                  (goldSoldBuyOrderData?.orderTempDetail ?? []).length <= 0 ||
                  hasSODOBilling ||
                  !isOrderOfAccountant
                }
                title="Nhập tay"
                handleOnclick={() =>
                  handleOpenModal(SelectModal.MANUALPROMOTION)
                }
              />
            )}
            {/* <Button
              disabled={isDisabledButton || !promotionData?.OutputDiscount}
              fill
              title="Áp dụng"
              handleOnclick={handleApplyVouchers}
            /> */}
            {/* <ButtonDownload
              disabled={
                isDisabledButton ||
                (goldSoldBuyOrderData?.orderTempDetail ?? []).length <= 0
              }
              onClick={() => console.log('first')}
            /> */}
          </div>
        </div>
        <CustomCollapse panels={panels} />
        <VoucherModal
          promotionData={promotionData}
          open={isModalVisible === SelectModal.VOUCHER}
          onCancel={handleCloseModal}
          onOk={handleConfirmVoucher}
          onBeforeOk={handleBeforeConfirmVoucher}
        />
        <ManualPromotionModal
          productsApplied={productsApplied}
          selectedRecord={selectedManualVoucher}
          data={promotionData?.Item?.results ?? []}
          open={isModalVisible === SelectModal.MANUALPROMOTION}
          orderData={orderData}
          onOk={handleConfirmManualVouchers}
          onCancel={handleCloseModal}
          onBeforeOk={handleBeforeConfirmManualVouchers}
        />
        <UpdateSAPPromotionModal
          open={isModalVisible === SelectModal.UPDATESAPPROMOTION}
          data={SAPPromotionsSelected}
          onCancel={handleCloseModal}
          onOk={handleConfirmUpdateSAPPromotion}
        />
      </Content>
    );
  },
);
